import { z } from "zod";

import { currency } from "@/hooks/firestore/models/currency";
import { timestamp } from "./timestamp";
import { SelectionsArraySchema, SelectionsMapSchema } from "./selection";
import { pickemsEntryData } from "./pickems";

const entryStatusEnum = z.enum([
  "CREATED",
  "ACCEPTED",
  "REJECTED",
  "LIVE",
  "SETTLED",
  "CANCELLED",
  "VOID",
]);

const betEntryResultEnum = z.enum([
  "UNDECIDED",
  "WIN",
  "LOSE",
  "VOID",
  "CASHED_OUT",
]);

const baseEntry = z.object({
  id: z.string().optional(),
  entryId: z.number(),
  referenceId: z.uuid(),
  entryType: z.enum(["BET", "PICKEM"]),
  entrySubtype: z.enum(["csgo-daily", "lol-daily", "dota2-daily"]).optional(),
  status: z.enum(["upcoming", "live", "historical", "hidden"]),
  count: z.number(),
  createdAtTs: timestamp,
  createdAt: z.iso.datetime(),
  updatedAtTs: timestamp,
  updatedAt: z.iso.datetime(),
  scheduledStartTimeTs: timestamp.optional(),
  scheduledStartTime: z.iso.datetime().optional(),
  hideStake: z.boolean().optional(),
  expiresAtTs: timestamp.optional(),
  shareId: z.string().optional(),
  shareName: z.string().optional(),
  sharedAt: timestamp.optional(),
  userId: z.string().optional(),
  data: z.record(z.string(), z.any()),
});

export const betEntry = baseEntry.extend({
  entryType: z.literal("BET"),
  data: z.object({
    id: z.string(),
    entryId: z.number(),
    status: entryStatusEnum,
    result: betEntryResultEnum,
    name: z.string(),
    stake: z.number(),
    currency,
    stakeSource: z.enum(["AVAILABLE", "PROMOTION_USER"]),
    paidOut: z.number(),
    potentialPayout: z.number(),
    createdAt: z.string(),
    updatedAt: z.string(),
    alternativeStakeReference: z.string().optional().nullish(),
    selectionsMap: SelectionsMapSchema.nullish(),
    selections: SelectionsArraySchema.nullish(),
    eventIds: z.array(z.string()).nullish(),
    automationProgressions: z
      .array(
        z.object({
          progressionId: z.string(),
        }),
      )
      .nullish(),
    selectionsRequiredToWin: z.number().nullish(),
    totalCombinations: z.number().nullish(),
    quaddieMarketId: z.string().nullish(),
    eligibleForPotentialCashout: z.boolean().nullish(),
    multiBuilderType: z.enum(["MYSTERY"]).nullish(),
    mysteryMultiLevel: z.number().nullish(),
    quaddieMarket: z
      .object({
        quaddieMarketId: z.string(),
        type: z.enum(["EARLY", "LATE", "TREBLE", "DAILY_DOUBLE", "CUSTOM"]),
        status: z.enum([
          "ACTIVE",
          "SUSPENDED",
          "HANDED_OVER",
          "SETTLED",
          "CANCELLED",
          "DEACTIVATED",
          "UNAVAILABLE",
          "INVALID",
        ]),
        scheduledStartTimeTs: timestamp.nullish(),
        scheduledStartTime: z.iso.datetime().nullish(),
      })
      .nullish(),
  }),
});

export const pickemsEntry = baseEntry.extend({
  entryType: z.literal("PICKEM"),
  data: pickemsEntryData,
});

export type BetEntryStatus = z.infer<typeof entryStatusEnum>;

export type BetEntryResult = z.infer<typeof betEntryResultEnum>;

export type BetEntry = z.infer<typeof betEntry>;

export type PickemsEntry = z.infer<typeof pickemsEntry>;

export const userEntry = z.discriminatedUnion("entryType", [
  betEntry,
  pickemsEntry,
]);

export type Entry = z.infer<typeof userEntry>;
